import * as React from "react";
import { Modal, Alert, Spin, Button, Space } from "antd";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useCharacterMap } from "../hooks/useCharacterMap";

interface CharacterMapSetupModalProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Modal for setting up character map configuration.
 *
 * Displays a QR code containing all characters used in SKU codes.
 * User scans this QR code with their barcode scanner to establish
 * the character mapping between scanner output and expected characters.
 */
export const CharacterMapSetupModal: React.FC<CharacterMapSetupModalProps> = ({
  visible,
  onClose,
}) => {
  const {
    source,
    target,
    isConfigured,
    isLoadingTarget,
    error,
    setSource,
    refreshTarget,
    clearConfiguration,
  } = useCharacterMap();

  const [scannedInput, setScannedInput] = React.useState<string>("");
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
  const [setupError, setSetupError] = React.useState<string | null>(null);
  const [setupSuccess, setSetupSuccess] = React.useState<boolean>(false);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (visible) {
      setScannedInput("");
      setSetupError(null);
      setSetupSuccess(false);
    }
  }, [visible]);

  // Handle keyboard input for scanning the QR code
  React.useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only process when modal is visible
      if (e.key.length === 1) {
        // Prevent other keyboard handlers from processing this input
        e.preventDefault();
        e.stopPropagation();
        // Accumulate characters
        setScannedInput((prev) => prev + e.key);
      } else if (e.key === "Enter") {
        // Prevent other keyboard handlers from processing this input
        e.preventDefault();
        e.stopPropagation();
        // Process the scanned code
        handleScanComplete();
      }
    };

    // Use capture phase to intercept events before other handlers
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [visible, scannedInput]);

  const handleScanComplete = async () => {
    if (!scannedInput || !target) return;

    setIsProcessing(true);
    setSetupError(null);

    try {
      // Validate that scanned length matches target length
      if (scannedInput.length !== target.length) {
        setSetupError(
          `Dĺžka zoskenovaného kódu (${scannedInput.length}) sa nezhoduje s počtom unikátnych znakov (${target.length}). Zmeňte klávesnicu.`
        );
        setScannedInput("");
        return;
      }

      // Set the source mapping
      await setSource(scannedInput);
      setSetupSuccess(true);

      // Auto-close after 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setSetupError(err instanceof Error ? err.message : "Failed to save character map");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearConfiguration = () => {
    clearConfiguration();
    setScannedInput("");
    setSetupError(null);
    setSetupSuccess(false);
  };

  return (
    <Modal
      title="Nastavenie mapovania znakov"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Loading State */}
        {isLoadingTarget && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Načítavam znaky...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert
            message="Chyba pri načítaní znakov"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={refreshTarget}>
                Skúsiť znova
              </Button>
            }
          />
        )}

        {/* Setup Instructions */}
        {!isLoadingTarget && !error && (
          <>
            {isConfigured ? (
              <Alert
                message="Mapovanie znakov nastavené"
                description="Mapovanie znakov je už nastavené a funguje. Môžete znova naskenovať QR kód pre nastavenie alebo vymazať konfiguráciu a začať odznova."
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
              />
            ) : (
              <Alert
                message="Vyžaduje sa nastavenie"
                description="Naskenujte QR kód nižšie s vašim čítačkou čiarových kódov pre nastavenie mapovania znakov. Toto umožní čítačke správne fungovať aj s neštandardnými rozloženiami klávesnice."
                type="info"
                showIcon
              />
            )}

            {/* QR Code Display */}
            {target && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px 0",
                  background: "#fff",
                }}
              >
                <QRCodeSVG value={target} size={256} level="M" />
              </div>
            )}

            {/* Scanning Feedback */}
            {scannedInput && !setupSuccess && (
              <Alert
                message="Skenujem..."
                description={`Prijaté ${scannedInput.length} znaky. Stlačte Enter alebo pokračujte v skenovaní.`}
                type="info"
                showIcon
              />
            )}

            {/* Setup Error */}
            {setupError && (
              <Alert
                message="Nastavenie zlyhalo"
                description={setupError}
                type="error"
                showIcon
                icon={<CloseCircleOutlined />}
              />
            )}

            {/* Setup Success */}
            {setupSuccess && (
              <Alert
                message="Nastavenie dokončené!"
                description="Mapovanie znakov bolo úspešne nastavené. Vaša čítačka teraz bude fungovať správne."
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
              />
            )}

            {/* Processing State */}
            {isProcessing && (
              <div style={{ textAlign: "center" }}>
                <Spin />
              </div>
            )}

            {/* Character Map Preview (Debug View) */}
            {isConfigured && (
              <div style={{ marginTop: 16 }}>
                <table
                  style={{
                    width: "100%",
                    fontSize: "12px",
                    border: "1px solid #d9d9d9",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#fafafa" }}>
                      <th style={{ padding: "8px", border: "1px solid #d9d9d9", textAlign: "left" }}>
                        Zdroj (Naskenované)
                      </th>
                      <th style={{ padding: "8px", border: "1px solid #d9d9d9", textAlign: "left" }}>
                        Cieľ (Očakávané)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          padding: "8px",
                          border: "1px solid #d9d9d9",
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                        }}
                      >
                        {source || "(prázdne)"}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          border: "1px solid #d9d9d9",
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                        }}
                      >
                        {target || "(prázdne)"}
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan={2}
                        style={{
                          padding: "8px",
                          border: "1px solid #d9d9d9",
                          fontSize: "11px",
                          color: "#666",
                        }}
                      >
                        Znaky: {target.length}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
              <Button onClick={onClose}>Zavrieť</Button>
              {isConfigured && (
                <Button danger onClick={handleClearConfiguration}>
                  Vymazať konfiguráciu
                </Button>
              )}
            </div>
          </>
        )}
      </Space>
    </Modal>
  );
};
